import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/HistoryPage";
import { PrimaryButton } from "@/components/FormBits";
import { Switch } from "@/components/ui/switch";
import { emptyPractice, GoalsFields, PracticeFields, practiceError } from "@/components/onboarding/ProfileFields";
import { LoveCards, PRO_USE_TEXT } from "@/components/onboarding/LoveCards";
import { DeleteAccount } from "@/components/DeleteAccount";

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-[#E6DBCF] bg-[#FDFBF7] p-6 sm:p-8">
      <h2 className="text-2xl font-semibold text-[#2C1810]">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [practice, setPractice] = useState({ ...emptyPractice, ...(user.profile || {}) });
  const [goals, setGoals] = useState(user.goals || []);
  const [busy, setBusy] = useState("");

  const save = async (key, req, check) => {
    const err = check?.();
    if (err) return toast.error(err);
    setBusy(key);
    try {
      const { data } = await req();
      setUser(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-6 py-10 sm:px-10 lg:py-14">
      <PageHead eyebrow="Account" title="Your profile" sub="Preferences personalize explanations only. They never replace consultation-specific assessment." />
      <Section title="Your practice">
        <PracticeFields value={practice} onChange={setPractice} />
        <PrimaryButton className="mt-6" disabled={busy === "p"} data-testid="profile-save-practice-btn"
          onClick={() => save("p", () => api.put("/onboarding/practice", practice), () => practiceError(practice))}>
          {busy === "p" && <Loader2 size={16} className="animate-spin" />} Save practice
        </PrimaryButton>
      </Section>
      <Section title="Your goals">
        <GoalsFields value={goals} onChange={setGoals} />
        <PrimaryButton className="mt-6" disabled={busy === "g"} data-testid="profile-save-goals-btn"
          onClick={() => save("g", () => api.put("/onboarding/goals", { goals }), () => (!goals.length ? "Please choose at least one goal." : ""))}>
          {busy === "g" && <Loader2 size={16} className="animate-spin" />} Save goals
        </PrimaryButton>
      </Section>
      <Section title="Communication">
        <p className="text-sm text-[#4A3B32]">Signed in as <span className="font-semibold" data-testid="profile-email">{user.email}</span> · {user.email_verified ? "email verified" : "email not yet verified"}</p>
        <label className="mt-4 flex items-center gap-3 text-sm text-[#4A3B32]">
          <Switch data-testid="profile-marketing-switch" checked={user.marketing_opt_in} disabled={busy === "m"}
            onCheckedChange={(v) => save("m", () => api.put("/profile/preferences", { marketing_opt_in: v }))} />
          Occasional MooreBeautyLove education updates and news
        </label>
        <label className="mt-4 flex items-center gap-3 text-sm text-[#4A3B32]">
          <Switch data-testid="profile-reminder-switch" checked={user.reminder_emails_opt_in !== false} disabled={busy === "r"}
            onCheckedChange={(v) => save("r", () => api.put("/profile/preferences", { reminder_emails_opt_in: v }))} />
          Reminder and tip emails (kit setup reminder, next-consultation tips)
        </label>
        <p className="mt-3 text-xs text-[#6E5549]">Essential account and security emails, such as verification and password resets, are always sent.</p>
      </Section>
      <Section title="How Ink Genius works">
        <LoveCards />
        <p className="mt-6 text-sm leading-relaxed text-[#6E5549]">{PRO_USE_TEXT}</p>
      </Section>
      {user.role !== "admin" && <Section title="Delete account"><DeleteAccount /></Section>}
    </div>
  );
}
